import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  defaultPricingContent,
  defaultAcceleratorContent,
  defaultSiteSettings,
  type AcceleratorContent,
  type PricingContent,
  type SiteSettings,
} from '../config/siteContent';
import { courseCatalogs, type CourseCatalog } from '../data/courseDetails';
import { siteContentApi } from '../utils/siteContentApi';

interface SiteContentContextValue {
  settings: SiteSettings;
  pricing: PricingContent;
  accelerator: AcceleratorContent;
  programs: CourseCatalog[];
  refreshContent: () => Promise<void>;
  setSettings: React.Dispatch<React.SetStateAction<SiteSettings>>;
  setPricing: React.Dispatch<React.SetStateAction<PricingContent>>;
  setAccelerator: React.Dispatch<React.SetStateAction<AcceleratorContent>>;
  setPrograms: React.Dispatch<React.SetStateAction<CourseCatalog[]>>;
}

const SiteContentContext = createContext<SiteContentContextValue | null>(null);

function buildContactLinks(contact: SiteSettings['contact']) {
  const digits = (value: string) => value.replace(/[^\d+]/g, '');
  const whatsappDigits = contact.whatsapp.replace(/\D/g, '');

  return {
    ...contact,
    phonePrimaryHref: `tel:${digits(contact.phonePrimary)}`,
    phoneSecondaryHref: `tel:${digits(contact.phoneSecondary)}`,
    whatsappHref: `https://wa.me/${whatsappDigits}`,
  };
}

export function SiteContentProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(defaultSiteSettings);
  const [pricing, setPricing] = useState<PricingContent>(defaultPricingContent);
  const [accelerator, setAccelerator] = useState<AcceleratorContent>(defaultAcceleratorContent);
  const [programs, setPrograms] = useState<CourseCatalog[]>(courseCatalogs);

  const refreshContent = useCallback(async () => {
    const [nextSettings, nextPricing, nextPrograms, nextAccelerator] = await Promise.all([
      siteContentApi.getSettings(),
      siteContentApi.getPricing(),
      siteContentApi.getPrograms(),
      siteContentApi.getAccelerator(),
    ]);
    setSettings(nextSettings);
    setPricing(nextPricing);
    setPrograms(nextPrograms);
    setAccelerator(nextAccelerator);
  }, []);

  useEffect(() => {
    refreshContent();
  }, [refreshContent]);

  const value = useMemo(
    () => ({
      settings: {
        ...settings,
        contact: buildContactLinks(settings.contact),
      } as SiteSettings,
      pricing,
      accelerator,
      programs,
      refreshContent,
      setSettings,
      setPricing,
      setAccelerator,
      setPrograms,
    }),
    [accelerator, pricing, programs, refreshContent, settings]
  );

  return <SiteContentContext.Provider value={value}>{children}</SiteContentContext.Provider>;
}

function useSiteContentContext() {
  const context = useContext(SiteContentContext);
  if (!context) {
    throw new Error('useSiteContentContext must be used within SiteContentProvider');
  }
  return context;
}

export function useSiteSettings() {
  return useSiteContentContext().settings;
}

export function usePricingContent() {
  return useSiteContentContext().pricing;
}

export function useAcceleratorContent() {
  return useSiteContentContext().accelerator;
}

export function useProgramsContent() {
  return useSiteContentContext().programs;
}

export function useSiteContentActions() {
  const { refreshContent, setAccelerator, setPricing, setPrograms, setSettings } = useSiteContentContext();
  return { refreshContent, setAccelerator, setPricing, setPrograms, setSettings };
}
